import { Select, Spin } from 'antd'
import { ComponentProps, useEffect } from 'react'
import { Bar } from 'react-chartjs-2'
import { useMunicipalityName, useResidencyCapacityAgeGroupTrendValues } from '../../../../api/benchmarking'
import BenchmarkingContentRow from '../../components/BenchmarkingContentRow'
import { BenchmarkingLegend } from '../../components/BenchmarkingLegend'
import { getColorByIndex } from '../../util/colors'
import { BORDER_WIDTH } from '../../util/constants'
import { getFontSize } from '../../util/fontSize'
import { baseGraphOptions } from '../../util/baseGraphOptions'

export const ResidencyCapacityAgeGroupTrend = ({ text }: { text: React.ReactNode }) => {
  const { data: municipalityName, isLoading: isMunicipalityNameLoading } = useMunicipalityName()

  const { data, isLoading, mutate } = useResidencyCapacityAgeGroupTrendValues()

  useEffect(() => {
    mutate({})
  }, [])

  if (isLoading) {
    return <BenchmarkingContentRow graph={<Spin size="large" />} text={text} />
  }

  if (!data || !municipalityName) {
    return <></>
  }

  const baselineOptions: ComponentProps<typeof Bar>['options'] = {
    ...baseGraphOptions,
    aspectRatio: 1,
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label} år: ${Math.round(context.parsed.y)}%`
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        min: 0,
        max: 100,
        ticks: {
          font: {
            size: getFontSize('md'),
          },
        },
      },
      y: {
        stacked: true,
        min: 0,
        max: 100,
        ticks: {
          callback: (value) => {
            return `${value}%`
          },
        },
      },
    },
  }

  const comparisonOptions: ComponentProps<typeof Bar>['options'] = {
    ...baseGraphOptions,
    aspectRatio: 1,
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label} år: ${Math.round(context.parsed.y)}%`
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        min: 0,
        max: 100,
        ticks: {
          font: {
            size: getFontSize('md'),
          },
        },
      },
      y: {
        stacked: true,
        min: 0,
        max: 100,
        ticks: {
          callback: (value) => {
            return `${value}%`
          },
        },
      },
    },
  }

  const baselineGraphData: ComponentProps<typeof Bar>['data'] = {
    labels: data.baselineValues?.[0]?.values?.map((item) => item.year) ?? [],
    datasets: data.baselineValues.map((baselineItem, index) => ({
      fill: false,
      label: baselineItem.ageRange,
      data: baselineItem.values?.map((item) => item.value),
      borderColor: getColorByIndex(index).border,
      backgroundColor: getColorByIndex(index).fill,
      borderWidth: BORDER_WIDTH,
    })),
  }
  const comparisonGraphData: ComponentProps<typeof Bar>['data'] = {
    labels: data.comparisonValues?.[0]?.values?.map((item) => item.year) ?? [],
    datasets: data.comparisonValues.map((comparisonItem, index) => ({
      fill: false,
      label: comparisonItem.ageRange,
      data: comparisonItem.values?.map((item) => item.value),
      borderColor: getColorByIndex(index).border,
      backgroundColor: getColorByIndex(index).fill,
      borderWidth: BORDER_WIDTH,
    })),
  }

  const dataSourceSelector = (
    <Select
      value={data.currentDataSource}
      onChange={(value) => {
        mutate({ datasource: value, grouping: undefined })
      }}
      options={data.dataSources.map((ds) => ({
        label: ds,
        value: ds,
      }))}
      style={{ width: '100%' }}
    />
  )

  const comparisonSelector = (
    <Select
      value={data.currentGrouping}
      onChange={(value) => {
        mutate({ grouping: value, datasource: data.currentVariation.comparison?.datasource?.name })
      }}
      options={data.groupings.map((grouping) => ({
        label: grouping,
        value: grouping,
      }))}
      style={{ width: '100%' }}
    />
  )

  const graph: React.ReactNode = (
    <div className="flex h-full items-start gap-2">
      <div className="h-full w-[30cqi] text-center">
        <p style={{ fontSize: getFontSize('md') }}>{municipalityName}</p>
        <Bar options={baselineOptions} data={baselineGraphData} />
      </div>
      <div className="h-full w-[30cqi] text-center">
        <p style={{ fontSize: getFontSize('md') }}>Nationellt</p>
        <Bar options={comparisonOptions} data={comparisonGraphData} />
      </div>
    </div>
  )

  const legend = (
    <BenchmarkingLegend
      items={data.baselineValues.map((item, index) => ({
        text: item.ageRange,
        fillColor: getColorByIndex(index).fill,
        borderColor: getColorByIndex(index).border,
      }))}
    />
  )

  const title = 'Andel boende per åldersgrupp'
  return <BenchmarkingContentRow graph={graph} dataSourceSelector={dataSourceSelector} comparisonSelector={comparisonSelector} text={text} title={title} legend={legend} />
}
